/**
 * Notification（通知中心）types.
 *
 * 字段形状对齐后端 notification 模块 wire 格式
 * （app/modules/notification/router.py：InboxItemResponse / TopicResponse /
 * PreferenceItem / PreferencesResponse / DigestFrequencyUpdate）。
 * 后端返回 snake_case（notification 模块未走 jerry 的 camelCase 分页约定），
 * api/notifications.ts 负责转成此处的 camelCase。
 */

/** P3 摘要邮件节奏（DigestFrequency：daily / hourly / off）。 */
export type NotificationDigestFrequency = "daily" | "hourly" | "off";

/** 送达渠道（NotificationChannel）。`""` 为主题级（全渠道）偏好行的哨兵值。 */
export type NotificationChannel = "in_app" | "sse" | "email";

/** 主题级偏好哨兵：channel="" 表示整主题开关（后端 NOTIFICATION_TOPIC_LEVEL）。 */
export const NOTIFICATION_TOPIC_LEVEL_CHANNEL = "";

/** 收件箱单条通知（NotificationInbox 行，camelCase 化后）。 */
export interface NotificationInboxItem {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  readAt: string | null;
  category: string | null;
  /** 深链锚（R4）：静态执行=execution.id、长程=agent_run.id、定时产物=run.id。 */
  refType: string | null;
  refId: string | null;
  createdAt: string;
}

/** 收件箱分页 meta（后端 snake_case page_size/total_pages 转camelCase）。 */
export interface NotificationInboxMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** 通知主题（GET /topics，用户偏好界面的数据源）。 */
export interface NotificationTopic {
  id: string;
  code: string;
  name: string;
  description: string;
  defaultPriority: string;
  isActive: boolean;
}

/** 单条偏好行。无行 = 默认订阅（opt-in）；topic 级行 channel=""。 */
export interface NotificationPreferenceItem {
  topicCode: string;
  channel: string;
  enabled: boolean;
}

/** GET /preferences 响应体。 */
export interface NotificationPreferences {
  preferences: NotificationPreferenceItem[];
  digestFrequency: NotificationDigestFrequency;
}

/** P3 摘要节奏中文标签（设置页选项）。 */
export const NOTIFICATION_DIGEST_FREQUENCY_LABELS: Record<NotificationDigestFrequency, string> = {
  daily: "每天汇总",
  hourly: "每小时汇总",
  off: "不发送摘要",
};

/** P3 摘要节奏可选项。 */
export const NOTIFICATION_DIGEST_FREQUENCY_OPTIONS: {
  value: NotificationDigestFrequency;
  label: string;
}[] = (
  Object.entries(NOTIFICATION_DIGEST_FREQUENCY_LABELS) as [
    NotificationDigestFrequency,
    string,
  ][]
).map(([value, label]) => ({ value, label }));
